import { Badge } from "V2.0/common/badge";

export const BULK_CAMPAIGN_STATUS = {
  SCHEDULED: "Scheduled",
  PROCESSING: "Processing",
  ONGOING: "Ongoing",
  PAUSED: "Paused",
  FAILED: "Failed",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
  QUOTA_EXHAUSTED: "QuotaExhausted",
  TELEPHONY_FAILURE: "TelephonyFailure",
  // Set by the backend when the campaign is paused because the assistant is outside its configured
  // office hours. Was missing here, so it fell through to the generic fallback below and rendered as
  // a grey badge showing the raw "OutsideOfficeHours" string.
  OUTSIDE_OFFICE_HOURS: "OutsideOfficeHours"
} as const;

export type BulkCampaignStatus =
  (typeof BULK_CAMPAIGN_STATUS)[keyof typeof BULK_CAMPAIGN_STATUS];

const bulkCampaignStatusConfig: Record<
  BulkCampaignStatus,
  { label: string; color: string }
> = {
  [BULK_CAMPAIGN_STATUS.COMPLETED]: {
    label: "Completed",
    color: "green",
  },
  [BULK_CAMPAIGN_STATUS.PROCESSING]: { label: "Processing", color: "amber" },
  [BULK_CAMPAIGN_STATUS.FAILED]: { label: "Failed", color: "red" },
  [BULK_CAMPAIGN_STATUS.ONGOING]: { label: "Ongoing", color: "yellow" },
  [BULK_CAMPAIGN_STATUS.PAUSED]: { label: "Paused", color: "purple" },
  [BULK_CAMPAIGN_STATUS.SCHEDULED]: { label: "Scheduled", color: "amber" },
  [BULK_CAMPAIGN_STATUS.CANCELLED]: { label: "Cancelled", color: "orange" },
  [BULK_CAMPAIGN_STATUS.QUOTA_EXHAUSTED]: { label: "Quota Exhausted", color: "red"},
  [BULK_CAMPAIGN_STATUS.TELEPHONY_FAILURE]: {label: "Telephony Failure", color: "red"},
  // Blue rather than red/amber: this is a normal, self-correcting wait (the campaign resumes on its
  // own once office hours start again), not a failure the user has to act on.
  [BULK_CAMPAIGN_STATUS.OUTSIDE_OFFICE_HOURS]: { label: "Outside Office Hours", color: "blue" }
} as const;

const statusTextColors: Record<string, string> = {
  red: "text-red-700 dark:text-red-400",
  orange: "text-orange-700 dark:text-orange-400",
  amber: "text-amber-700 dark:text-amber-400",
  yellow: "text-yellow-700 dark:text-yellow-300",
  purple: "text-purple-700 dark:text-purple-400",
  blue: "text-blue-700 dark:text-blue-400",
  green: "text-green-700 dark:text-green-400",
  zinc: "text-zinc-700 dark:text-zinc-400",
};

export default function BulkCampaignStatus(props: {
  status: BulkCampaignStatus;
  variant?: "badge" | "text";
}) {
  const { status, variant = "badge" } = props;

  const { color, label } = bulkCampaignStatusConfig[status] ?? {
    color: "zinc",
    label: status,
  };

  if (variant === "text") {
    return (
      <span className={`font-medium ${statusTextColors[color] ?? statusTextColors.zinc}`}>
        {label}
      </span>
    );
  }

  return <Badge color={color}>{label}</Badge>;
}
